import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Loader2, CheckCircle, AlertCircle, Clock, Database,
  ChevronDown, ChevronUp, Zap, FileText, X, Server, Copy, Check
} from 'lucide-react';
import apiClient from '../../lib/apiClient';
import './ActiveScrapePanel.css';

function ActiveScrapePanel({ jobId, onDone, onCancel }) {
  const [expanded, setExpanded] = useState(true);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [copied, setCopied] = useState(false);
  const startTimeRef = useRef(Date.now());
  const timerRef = useRef(null);

  // Poll job status every 2 seconds
  const { data: jobData } = useQuery({
    queryKey: ['scraper', 'job', jobId],
    queryFn: async () => {
      const { jobs } = await apiClient.get('/api/scraper/jobs?limit=20');
      return jobs?.find(j => j.id === jobId) || null;
    },
    refetchInterval: 2000,
    enabled: !!jobId,
  });

  const job = jobData || null;
  const params = job ? (typeof job.params === 'string' ? JSON.parse(job.params) : (job.params || {})) : {};
  const result = job ? (typeof job.result === 'string' ? JSON.parse(job.result) : (job.result || {})) : {};
  const status = job?.status || 'pending';

  const isTerminal = ['completed', 'partial', 'failed', 'cancelled'].includes(status);
  const isSuccess = status === 'completed' || status === 'partial';
  const isFailed = status === 'failed' || status === 'cancelled';
  const isRunning = ['running', 'claimed', 'downloading', 'downloaded', 'persisting'].includes(status);
  const isPending = status === 'pending';
  // Live phase/percent reported by the worker via PUT /jobs/:id/progress.
  // Falls back to status-derived label when the worker hasn't posted yet.
  const progress = typeof job?.progress === 'string' ? JSON.parse(job.progress) : (job?.progress || {});

  // Fetch logs for this job (in-memory first, fallback to history)
  const { data: logsData } = useQuery({
    queryKey: ['scraper', 'logs', jobId],
    queryFn: () => apiClient.get(`/api/scraper/logs?jobId=${jobId}&limit=500`),
    refetchInterval: isTerminal ? false : 3000,
    enabled: !!jobId,
  });

  const jobLogs = logsData?.logs || logsData || [];

  // Copy job context + logs to clipboard
  const handleCopyLogs = useCallback(async () => {
    const lines = [
      `=== Scraper Job ${jobId} ===`,
      `Status: ${status}`,
      `Params: ${JSON.stringify(params, null, 2)}`,
    ];
    if (Object.keys(result).length > 0) {
      lines.push(`Result: ${JSON.stringify(result, null, 2)}`);
    }
    if (job?.error) {
      lines.push(`Error: ${job.error}`);
    }
    if (job?.worker_id) {
      lines.push(`Worker: ${job.worker_id}`);
    }
    if (job?.started_at) {
      lines.push(`Started: ${job.started_at}`);
    }
    if (job?.completed_at) {
      lines.push(`Completed: ${job.completed_at}`);
    }

    lines.push('', `=== Logs (${jobLogs.length} entries) ===`);

    const logEntries = Array.isArray(jobLogs) ? jobLogs : [];
    for (const log of logEntries) {
      const ts = log.timestamp ? new Date(log.timestamp).toISOString() : '';
      const ctx = log.context ? ` ${JSON.stringify(log.context)}` : '';
      lines.push(`[${ts}] [${(log.level || 'info').toUpperCase()}] ${log.message || ''}${ctx}`);
    }

    await navigator.clipboard.writeText(lines.join('\n'));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [jobId, status, params, result, job, jobLogs]);

  // Elapsed timer
  useEffect(() => {
    if (job?.started_at) {
      startTimeRef.current = new Date(job.started_at).getTime();
    }
    if (!isTerminal) {
      timerRef.current = setInterval(() => {
        setElapsedTime(Math.floor((Date.now() - startTimeRef.current) / 1000));
      }, 1000);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [isTerminal, job?.started_at]);

  // Notify parent when job finishes
  useEffect(() => {
    if (isTerminal && onDone) {
      const timeout = setTimeout(() => onDone(job), 3000);
      return () => clearTimeout(timeout);
    }
  }, [isTerminal, onDone, job]);

  // Duration from result or elapsed timer
  const duration = isTerminal && result.duration
    ? result.duration
    : elapsedTime;

  const suppliersFound = result.suppliers || result.saved || 0;
  const pagesProcessed = result.pages || 0;
  const totalExpected = result.totalExpected || null;
  const newCount = result.newCount;
  const dupCount = result.duplicateCount;
  const hasSubJobs = !!params.parentJobId;

  // Stage label — prefer the live progress.phase reported by the worker,
  // then map to status when the worker hasn't posted a phase yet.
  const phaseFromProgress = progress.phase
    ? progress.phase.charAt(0).toUpperCase() + progress.phase.slice(1)
      + (progress.percent ? ` ${progress.percent}%` : '')
    : null;
  const stageLabel = phaseFromProgress
    ? phaseFromProgress
    : isPending ? 'Waiting for worker...'
    : status === 'claimed' ? 'Worker claimed job...'
    : status === 'downloading' ? 'Downloading from CaleProcure...'
    : status === 'downloaded' ? 'Queued for persistence...'
    : status === 'persisting' ? 'Saving to database...'
    : isRunning ? 'Scraping...'
    : isSuccess ? 'Complete'
    : isFailed ? (job?.error || 'Failed')
    : status;

  const formatTime = (seconds) => {
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  // Build label from params
  const jobLabel = params.departmentCode
    ? `${params.departmentCode} · ${params.acquisitionType || 'All Types'}`
    : params.acquisitionType || 'SCPRS Scrape';

  const dateRange = params.dateFrom && params.dateTo
    ? `${params.dateFrom} → ${params.dateTo}`
    : null;

  return (
    <div className={`asp-container ${isTerminal ? (isSuccess ? 'asp-success' : 'asp-error') : 'asp-active'}`}>
      {/* Header */}
      <div className="asp-header" onClick={() => setExpanded(!expanded)}>
        <div className="asp-header-left">
          {!isTerminal && <Loader2 size={16} className="asp-spin" />}
          {isSuccess && <CheckCircle size={16} className="asp-icon-success" />}
          {isFailed && <AlertCircle size={16} className="asp-icon-error" />}

          <span className="asp-title">
            {isPending && 'Job Queued'}
            {isRunning && 'Scrape in Progress'}
            {status === 'claimed' && 'Worker Starting'}
            {isSuccess && (status === 'partial' ? 'Scrape Partial' : 'Scrape Completed')}
            {isFailed && 'Scrape Failed'}
          </span>

          <span className="asp-params">{jobLabel}</span>
        </div>

        <div className="asp-header-right">
          <span className="asp-timer">
            <Clock size={12} />
            {formatTime(duration)}
          </span>
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
      </div>

      {expanded && (
        <div className="asp-body">
          {/* Progress Stats */}
          <div className="asp-stats">
            <div className="asp-stat">
              <Database size={14} />
              <span className="asp-stat-value">{suppliersFound}</span>
              <span className="asp-stat-label">
                POs{totalExpected ? ` of ${totalExpected}` : ''}
              </span>
            </div>
            {pagesProcessed > 0 && (
              <div className="asp-stat">
                <FileText size={14} />
                <span className="asp-stat-value">{pagesProcessed}</span>
                <span className="asp-stat-label">pages</span>
              </div>
            )}
            {newCount != null && (
              <div className="asp-stat">
                <Zap size={14} />
                <span className="asp-stat-value">{newCount} new</span>
                <span className="asp-stat-label">{dupCount || 0} dup</span>
              </div>
            )}
            <div className="asp-stat asp-stat-stage">
              {isRunning && <Loader2 size={12} className="asp-spin" />}
              <span className="asp-stat-value">{stageLabel}</span>
            </div>
          </div>

          {/* Date range info */}
          {dateRange && (
            <div className="asp-meta">
              <Clock size={12} /> {dateRange}
              {job?.worker_id && <><Server size={12} /> {job.worker_id}</>}
            </div>
          )}

          {/* Progress Bar — prefer live progress.percent from the worker,
              fall back to suppliersFound/totalExpected ratio, then indeterminate */}
          {!isTerminal && (
            <div className="asp-progress">
              <div className="asp-progress-bar">
                {progress.percent > 0 ? (
                  <div
                    className="asp-progress-fill"
                    style={{ width: `${Math.min(100, progress.percent)}%` }}
                  />
                ) : totalExpected && suppliersFound > 0 ? (
                  <div
                    className="asp-progress-fill"
                    style={{ width: `${Math.min(100, Math.round(suppliersFound / totalExpected * 100))}%` }}
                  />
                ) : (
                  <div className="asp-progress-fill asp-progress-indeterminate" />
                )}
              </div>
            </div>
          )}

          {/* Result Summary */}
          {isTerminal && (
            <div className={`asp-result ${isSuccess ? 'asp-result-success' : 'asp-result-error'}`}>
              {isSuccess ? (
                <>
                  <CheckCircle size={14} />
                  <span>
                    Captured {suppliersFound} POs in {formatTime(result.duration || 0)}
                    {status === 'partial' && totalExpected ? ` (capped — ${totalExpected} available)` : ''}
                  </span>
                </>
              ) : (
                <>
                  <AlertCircle size={14} />
                  <span>{job?.error || 'Scrape failed'}</span>
                </>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="asp-actions">
            <button className="asp-copy-btn" onClick={handleCopyLogs} title="Copy job details + logs to clipboard">
              {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy Logs</>}
            </button>
            {!isTerminal && onCancel && (
              <button className="asp-cancel-btn" onClick={onCancel}>
                <X size={14} />
                Cancel
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default ActiveScrapePanel;
